import { api } from '../../services/api';
import {
  mapSiteConditionState,
  type ContinuousSafetyFetchResult,
  type SiteConditionState,
} from './siteConditionState';

export type ConditionWorkspaceAttention = {
  attentionId: string;
  kind: string;
  conditionId: string;
  detail: Record<string, unknown>;
};

export type ConditionWorkspaceWorkItem = {
  conditionId: string;
  linkId: string;
  objectType: string;
  objectId: string;
  relationship: string;
  status: string | null;
  ownerId: string | null;
  dueAt: string | null;
};

export type ConditionWorkspaceScan = {
  conditionId: string;
  triggerType: string | null;
  status: string | null;
  windowStart: string | null;
  windowEnd: string | null;
  eventIds: string[];
};

export type ConditionWorkspaceRecord = {
  conditionId: string;
  recordType: string;
  objectType: string;
  objectId: string;
};

export type ConditionWorkspaceResponse = {
  ok: boolean;
  orgId: string;
  siteId: string;
  queryAsOf: string;
  conditions: SiteConditionState[];
  work: ConditionWorkspaceWorkItem[];
  scans: ConditionWorkspaceScan[];
  records: ConditionWorkspaceRecord[];
  attention: ConditionWorkspaceAttention[];
  counts: {
    conditions: number;
    work: number;
    scans: number;
    records: number;
    history: number;
    attention: number;
  };
};

function mapAttention(row: Record<string, unknown>): ConditionWorkspaceAttention {
  return {
    attentionId: String(row.attention_id || ''),
    kind: String(row.kind || ''),
    conditionId: String(row.condition_id || ''),
    detail: (row.detail as Record<string, unknown>) || {},
  };
}

function mapWork(row: Record<string, unknown>): ConditionWorkspaceWorkItem {
  return {
    conditionId: String(row.condition_id || ''),
    linkId: String(row.link_id || ''),
    objectType: String(row.object_type || ''),
    objectId: String(row.object_id || ''),
    relationship: String(row.relationship || ''),
    status: row.status == null ? null : String(row.status),
    ownerId: row.owner_id == null ? null : String(row.owner_id),
    dueAt: row.due_at == null ? null : String(row.due_at),
  };
}

function mapScan(row: Record<string, unknown>): ConditionWorkspaceScan {
  const run = (row.run as Record<string, unknown>) || {};
  return {
    conditionId: String(row.condition_id || ''),
    triggerType: run.trigger_type == null ? null : String(run.trigger_type),
    status: run.status == null ? null : String(run.status),
    windowStart: run.window_start == null ? null : String(run.window_start),
    windowEnd: run.window_end == null ? null : String(run.window_end),
    eventIds: Array.isArray(row.event_ids)
      ? row.event_ids.map((item) => String(item))
      : [],
  };
}

function mapRecord(row: Record<string, unknown>): ConditionWorkspaceRecord {
  return {
    conditionId: String(row.condition_id || ''),
    recordType: String(row.record_type || row.object_type || ''),
    objectType: String(row.object_type || ''),
    objectId: String(row.object_id || row.record_id || ''),
  };
}

export function mapConditionWorkspace(
  payload: Record<string, unknown>,
): ConditionWorkspaceResponse {
  const counts = (payload.counts as Record<string, unknown>) || {};
  return {
    ok: true,
    orgId: String(payload.org_id || ''),
    siteId: String(payload.site_id || ''),
    queryAsOf: String(payload.query_as_of || ''),
    conditions: Array.isArray(payload.conditions)
      ? payload.conditions.map((row) => mapSiteConditionState(row as Record<string, unknown>))
      : [],
    work: Array.isArray(payload.work)
      ? payload.work.map((row) => mapWork(row as Record<string, unknown>))
      : [],
    scans: Array.isArray(payload.scans)
      ? payload.scans.map((row) => mapScan(row as Record<string, unknown>))
      : [],
    records: Array.isArray(payload.records)
      ? payload.records.map((row) => mapRecord(row as Record<string, unknown>))
      : [],
    attention: Array.isArray(payload.attention)
      ? payload.attention.map((row) => mapAttention(row as Record<string, unknown>))
      : [],
    counts: {
      conditions: Number(counts.conditions ?? 0),
      work: Number(counts.work ?? 0),
      scans: Number(counts.scans ?? 0),
      records: Number(counts.records ?? 0),
      history: Number(counts.history ?? 0),
      attention: Number(counts.attention ?? 0),
    },
  };
}

export async function fetchConditionWorkspace(params: {
  siteId: string;
  conditionIds?: string[];
  asOf?: string;
}): Promise<ContinuousSafetyFetchResult<ConditionWorkspaceResponse>> {
  const search = new URLSearchParams();
  for (const conditionId of params.conditionIds || []) {
    search.append('condition_id', conditionId);
  }
  if (params.asOf) {
    search.set('as_of', params.asOf);
  }
  const query = search.toString();
  const path = `/api/v1/sites/${encodeURIComponent(params.siteId)}/condition-workspace${
    query ? `?${query}` : ''
  }`;
  try {
    const response = await api.get(path);
    const payload = (response.data || {}) as Record<string, unknown>;
    if (!payload?.ok) {
      return {
        status: 'error',
        httpStatus: response.status || null,
        message: 'Condition workspace returned an invalid response.',
      };
    }
    return { status: 'success', data: mapConditionWorkspace(payload) };
  } catch (error) {
    const httpStatus = Number(
      (error as { response?: { status?: number } })?.response?.status || 0,
    ) || null;
    if (httpStatus === 404) {
      return { status: 'disabled' };
    }
    return {
      status: 'error',
      httpStatus,
      message: httpStatus === 401 || httpStatus === 403
        ? 'Condition workspace is not authorized for this session.'
        : 'Condition workspace is temporarily unavailable.',
    };
  }
}
